import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteConfigProvider } from "@/lib/siteConfig";
import { getSEO } from "@/lib/seo";
import { PageSchema } from "@/lib/schemas";
import { CartProvider, CartFAB, CartPanel } from "@/components/shared/Cart";
import { PriceModeProvider } from "@/lib/priceMode";

// Layout
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";

// Pages
import HomePage from "@/pages/HomePage";
import SolutionsHub from "@/pages/SolutionsHub";
import SolutionPage from "@/pages/SolutionPage";
import HardwareHub from "@/pages/HardwareHub";
import HardwareCategoryPage from "@/pages/HardwareCategoryPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import SoftwareHub from "@/pages/SoftwareHub";
import SoftwarePage from "@/pages/SoftwarePage";
import VenddiSoftwarePage from "@/pages/VenddiSoftwarePage";
import IAPage from "@/pages/IAPage";
import IASolutionsHub from "@/pages/IASolutionsHub";
import AVXPage from "@/pages/AVXPage";
import OpenClawPage from "@/pages/OpenClawPage";
import ServicePage from "@/pages/ServicePage";
import CasesHub from "@/pages/CasesHub";
import CaseDetailPage from "@/pages/CaseDetailPage";
import CEOCockpitPage from "@/pages/CEOCockpitPage";
import CSuitePage from "@/pages/CSuitePage";
import ContactPage from "@/pages/ContactPage";
import GraciasPage from "@/pages/GraciasPage";
import { NosotrosPage, HistoriaPage, PartnersPage, CertificacionesPage, NoticiasPage, ServicesHub } from "@/pages/CompanyPages";
import { OperacionEnVivoPage } from "@/pages/OperacionEnVivoPage";
import CalculadoraROIPage from "@/pages/CalculadoraROIPage";
import ProgramaPilotoPage from "@/pages/ProgramaPilotoPage";
import ArriendoLeasingPage from "@/pages/ArriendoLeasingPage";
import MiCotizacionPage from "@/pages/MiCotizacionPage";
import NovedadesPage from "@/pages/NovedadesPage";
import CrewPage from "@/pages/CrewPage";
import CorporateInfoPage from "@/pages/CorporateInfoPage";
import AdminPanel from "@/pages/AdminPanel";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// SEO: Dynamic meta tags per page
const PageSEO = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const seo = getSEO(pathname);
    // Always use production domain for canonical/og URLs so preview and prod
    // share the same SEO surface (no duplicate-content split)
    const PROD_BASE = 'https://allcom.cl';

    // Title
    document.title = seo.title;

    // Helper to set/create meta tag
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    // Description
    setMeta('name', 'description', seo.description);
    setMeta('name', 'keywords', seo.keywords);

    // Open Graph
    setMeta('property', 'og:title', seo.title);
    setMeta('property', 'og:description', seo.description);
    setMeta('property', 'og:url', `${PROD_BASE}${pathname}`);

    // Twitter
    setMeta('name', 'twitter:title', seo.title);
    setMeta('name', 'twitter:description', seo.description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${PROD_BASE}${pathname}`);

  }, [pathname]);
  return null;
};

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <div className={`flex flex-col min-h-screen ${isAdmin ? '' : 'bg-[#F8FAFC]'}`}>
      {!isAdmin && <Header />}
      <main className="flex-1" id="main-content" role="main">
        <Routes>
          {/* Home */}
          <Route path="/" element={<HomePage />} />
          
          {/* Solutions */}
          <Route path="/soluciones" element={<SolutionsHub />} />
          <Route path="/soluciones/visual-experience" element={<AVXPage />} />
          <Route path="/soluciones/:slug" element={<SolutionPage />} />
          
          {/* Hardware */}
          <Route path="/hardware" element={<HardwareHub />} />
          <Route path="/hardware/:slug" element={<HardwareCategoryPage />} />
          <Route path="/hardware/:categorySlug/:productSlug" element={<ProductDetailPage />} />
          
          {/* Software */}
          <Route path="/software" element={<SoftwareHub />} />
          <Route path="/software/venddi" element={<VenddiSoftwarePage />} />
          <Route path="/software/:slug" element={<SoftwarePage />} />
          
          {/* IA */}
          <Route path="/ia-soluciones" element={<IASolutionsHub />} />
          <Route path="/ia/allcom-ia" element={<IAPage />} />
          <Route path="/ia/calculador-de-roi" element={<CalculadoraROIPage />} />
          <Route path="/ia/dashboards-ejecutivos" element={<IAPage />} />
          <Route path="/ia/agentes-ia" element={<IAPage />} />
          <Route path="/ia/openclaw" element={<OpenClawPage />} />
          
          {/* Services */}
          <Route path="/servicios" element={<ServicesHub />} />
          <Route path="/servicios/:slug" element={<ServicePage />} />
          
          {/* Cases */}
          <Route path="/casos" element={<CasesHub />} />
          <Route path="/casos/:slug" element={<CaseDetailPage />} />
          
          {/* Demo Dashboards */}
          <Route path="/ceocockpit" element={<CEOCockpitPage />} />
          <Route path="/csuite" element={<CSuitePage />} />
          
          {/* Company */}
          <Route path="/empresa/nosotros" element={<NosotrosPage />} />
          <Route path="/empresa/historia" element={<HistoriaPage />} />
          <Route path="/empresa/equipo" element={<CrewPage />} />
          <Route path="/empresa/partners" element={<PartnersPage />} />
          <Route path="/empresa/certificaciones" element={<CertificacionesPage />} />
          <Route path="/empresa/operacion-en-vivo" element={<OperacionEnVivoPage />} />
          <Route path="/empresa/noticias" element={<NoticiasPage />} />
          <Route path="/empresa/informacion-corporativa" element={<CorporateInfoPage />} />
          
          {/* Contact */}
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/servicios/programa-piloto" element={<ProgramaPilotoPage />} />
          <Route path="/servicios/arriendo-leasing" element={<ArriendoLeasingPage />} />
          <Route path="/gracias" element={<GraciasPage />} />
          
          {/* Admin */}
          <Route path="/admin/*" element={<AdminPanel />} />
          {/* B2B Quote */}
          <Route path="/mi-cotizacion" element={<MiCotizacionPage />} />
          {/* Novedades / Releases */}
          <Route path="/novedades" element={<NovedadesPage />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppFAB />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SiteConfigProvider>
        <CartProvider>
        <PriceModeProvider>
        <ScrollToTop />
        <PageSEO />
        <PageSchema />
        <AppContent />
        <CartFAB />
        <CartPanel />
        <Toaster position="top-right" richColors />
        </PriceModeProvider>
        </CartProvider>
      </SiteConfigProvider>
    </BrowserRouter>
  );
}

export default App;
